import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Building2, Search, MapPin, Clock, Languages, ExternalLink, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { EmbassyEntry } from "@/content/embassies/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

const CONSUL_COUNT = 21;
const FAQS = [
  {
    q: "ต้องไปสถานทูตในกรุงเทพหรือทำที่ภูเก็ตได้?",
    a: `ขึ้นกับประเทศ — ${CONSUL_COUNT} ประเทศมี Honorary Consul ในภูเก็ต ทำเอกสารพื้นฐาน (สำเนา, POA, Single Status) ได้เลย ส่วนเอกสารทางการ (CNI, Apostille) มักต้องผ่านสถานทูตในกรุงเทพ — ทีมเรารับ-ส่งเอกสารแทนได้`,
  },
  {
    q: "ประเทศไหนใช้ Apostille บ้าง?",
    a: "ปัจจุบันมีกว่า 130 ประเทศภาคีที่เข้าร่วม Hague Apostille Convention เช่น ออสเตรเลีย, นิวซีแลนด์, ญี่ปุ่น, เกาหลีใต้, จีน (พ.ย. 2023), สิงคโปร์, อินเดีย, แคนาดา (ม.ค. 2024), เนเธอร์แลนด์, สวีเดน — ไทยยื่นภาคยานุวัติสารแล้วเมื่อ 30 มิ.ย. 2569 และอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2570 — ระหว่างนี้ยังใช้ระบบ MFA Legalization",
  },
  {
    q: "ใช้เวลานานเท่าไหร่กว่าจะได้เอกสารกลับมา?",
    a: "Honorary Consul ในภูเก็ต 1–3 วัน, สถานทูตในกรุงเทพ 3–14 วัน, MFA Legalization 2 วันทำการ (ด่วน) หรือ 4 วันทำการ (ปกติ) — ทีมเราเตรียมเอกสารและจัดคิวให้ จึงไม่ต้องเดินทางหลายรอบ",
  },
  {
    q: "ต้องแปลเอกสารก่อนรับรองหรือไม่?",
    a: "ใช่ — เอกสารภาษาไทยต้องแปลเป็นภาษาทางการของประเทศปลายทาง (หรือภาษาอังกฤษ) โดยนักแปลที่ขึ้นทะเบียน MoJ ก่อน จึงจะรับรองที่สถานทูตได้ ทีมเราแปลกว่า 40 ภาษา รวมถึง NAATI สำหรับออสเตรเลีย",
  },
  {
    q: "ค่าใช้จ่ายในการรับรองที่สถานทูตประมาณเท่าไหร่?",
    a: "ค่าธรรมเนียมสถานทูตแตกต่างกันมาก: USA ~$50/เอกสาร, UK £30–£75, EU €30–€80, Australia AUD $93, Japan ¥2,500–¥6,000 — ทีมเราคิดค่าบริการรับ-ส่งและจัดเตรียมเริ่ม 2,500 บาท/รอบ",
  },
];

type EmbassyLoaderData = {
  embassies: EmbassyEntry[];
  regions: { key: EmbassyEntry["region"]; label: string; emoji: string }[];
  total: number;
  consulCount: number;
};

export const Route = createFileRoute("/embassies/")({
  component: EmbassiesHub,
  loader: async (): Promise<EmbassyLoaderData> => {
    const {
      EMBASSIES,
      EMBASSY_REGIONS,
      EMBASSY_TOTAL,
      PHUKET_CONSUL_COUNT,
    } = await import("@/content/embassies/registry");
    return {
      embassies: EMBASSIES,
      regions: EMBASSY_REGIONS as unknown as EmbassyLoaderData["regions"],
      total: EMBASSY_TOTAL,
      consulCount: PHUKET_CONSUL_COUNT,
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const EMBASSIES = loaderData?.embassies ?? [];
    const EMBASSY_TOTAL = loaderData?.total ?? EMBASSIES.length;
    const PHUKET_CONSUL_COUNT = loaderData?.consulCount ?? CONSUL_COUNT;
    const base = pageHeadForProvince({
      title: `รวมสถานทูต & กงสุล ${EMBASSY_TOTAL}+ ประเทศ — รับรองเอกสารครบจบ | Thai Notary`,
      description: `ไดเรกทอรีสถานทูตในกรุงเทพ ${EMBASSY_TOTAL} ประเทศ + กงสุลกิตติมศักดิ์ในภูเก็ต ${PHUKET_CONSUL_COUNT} ประเทศ — เวลาที่ใช้, เอกสารที่รับรองได้, ภาษาแปล, ลิงก์บริการ Apostille / MFA / Embassy Legalization`,
      path: "/embassies",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "สถานทูต & กงสุล", url: "/embassies" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Embassies & Consulates Directory — ${EMBASSY_TOTAL} countries`,
            itemListElement: EMBASSIES.map((e, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${e.nameTh} (${e.nameEn})`,
              url: `${site}/embassies/${e.code}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(FAQS)),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/embassies")) },
      ],
    };
  },
});

type RegionKey = EmbassyEntry["region"] | "all" | "phuket";

function EmbassiesHub() {
  const {
    embassies: EMBASSIES,
    regions: EMBASSY_REGIONS,
    total: EMBASSY_TOTAL,
    consulCount: PHUKET_CONSUL_COUNT,
  } = Route.useLoaderData() as EmbassyLoaderData;
  const [region, setRegion] = useState<RegionKey>("all");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return EMBASSIES.filter((e) => {
      const matchRegion =
        region === "all" ||
        (region === "phuket" ? e.phuketConsul : e.region === region);
      const matchQ =
        !norm ||
        e.nameTh.toLowerCase().includes(norm) ||
        e.nameEn.toLowerCase().includes(norm) ||
        e.code.includes(norm);
      return matchRegion && matchQ;
    });
  }, [region, q]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Building2 className="h-3.5 w-3.5" /> Directory · {EMBASSY_TOTAL} embassies · {PHUKET_CONSUL_COUNT} Phuket consuls
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              สถานทูต & กงสุลกิตติมศักดิ์ในไทย — รับรองเอกสาร {EMBASSY_TOTAL}+ ประเทศ
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              ไดเรกทอรีครบ — สถานทูตในกรุงเทพ {EMBASSY_TOTAL} ประเทศ พร้อมข้อมูล{" "}
              <strong>เวลาที่ใช้, เอกสารที่รับรองได้, ภาษาที่ต้องแปล</strong>, สถานะ Apostille
              และ <strong>กงสุลกิตติมศักดิ์ในภูเก็ต {PHUKET_CONSUL_COUNT} ประเทศ</strong> —
              ทีมเราจัดเตรียม, แปล MoJ/NAATI, ส่งรับรอง MFA และนำส่งสถานทูตให้
            </p>
            <div id="ai-answer-tldr" className="mt-6 rounded-lg border-l-4 border-gold bg-card p-4 text-sm">
              <div className="text-xs font-bold uppercase tracking-widest text-gold">TL;DR (AI Answer)</div>
              <p className="mt-2 leading-relaxed text-foreground/90">
                สถานทูตส่วนใหญ่อยู่ในกรุงเทพ (ถ.วิทยุ, ถ.สาทร, ถ.เจริญกรุง) — มี{" "}
                <strong>{PHUKET_CONSUL_COUNT} ประเทศที่มี Honorary Consul ในภูเก็ต</strong>{" "}
                ทำเอกสารพื้นฐานได้ทันที — ประเทศที่เข้าร่วม Apostille (~130 ประเทศภาคี) ใช้
                ขั้นตอน MFA + Apostille แทน Embassy Legalization
              </p>
            </div>
            <div className="mt-6 relative max-w-xl">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ค้นหา เช่น อังกฤษ, USA, Japan, au…"
                className="w-full rounded-lg border border-border bg-card pl-10 pr-4 py-3 text-sm placeholder:text-muted-foreground focus:border-gold focus:outline-none"
              />
            </div>
          </div>
        </section>

        <section className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
            <button
              onClick={() => setRegion("all")}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${region === "all" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-muted/80"}`}
            >
              ทั้งหมด ({EMBASSIES.length})
            </button>
            <button
              onClick={() => setRegion("phuket")}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${region === "phuket" ? "bg-gold text-primary" : "bg-muted text-foreground hover:bg-muted/80"}`}
            >
              ⭐ มีกงสุลในภูเก็ต ({PHUKET_CONSUL_COUNT})
            </button>
            {EMBASSY_REGIONS.map((r) => {
              const count = EMBASSIES.filter((e) => e.region === r.key).length;
              return (
                <button
                  key={r.key}
                  onClick={() => setRegion(r.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${region === r.key ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-muted/80"}`}
                >
                  {r.emoji} {r.label} ({count})
                </button>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-10">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground">ไม่พบประเทศที่ตรงกับ "{q}"</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((e) => (
                <Link
                  key={e.code}
                  to="/embassies/$code"
                  params={{ code: e.code }}
                  className="group rounded-xl border border-border bg-card p-5 transition hover:border-gold hover:shadow-lg"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-2xl">{e.flag}</div>
                      <h2 className="mt-2 text-base font-bold leading-tight group-hover:text-gold">
                        {e.nameTh}
                      </h2>
                      <p className="text-xs text-muted-foreground">{e.nameEn}</p>
                    </div>
                    {e.phuketConsul && (
                      <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold">
                        ภูเก็ต
                      </span>
                    )}
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" /> {e.embassyCity === "Bangkok" ? "กรุงเทพ" : "อื่นๆ"}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3 w-3" /> {e.processingDays}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Languages className="h-3 w-3" /> {e.translationLanguages.join(", ")}
                    </div>
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground/80">
                    {e.intro}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="border-t border-border bg-muted/30">
          <div className="mx-auto max-w-4xl px-4 py-14">
            <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อย (FAQ)</h2>
            <div className="mt-6 space-y-4">
              {FAQS.map((f, i) => (
                <details key={i} className="group rounded-lg border border-border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-bold text-foreground group-open:text-gold">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">ต้องรับรองเอกสารกับสถานทูตไหน?</h2>
            <p className="mt-3 text-sm text-primary-foreground/90">
              ทีมเราจัดเตรียม → แปล MoJ/NAATI → ส่ง MFA → นำเข้าสถานทูต ครบจบในจุดเดียว
              เริ่ม 2,500 บาท/รอบ
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-3 text-sm font-bold text-primary hover:bg-gold/90"
            >
              <CheckCircle2 className="h-4 w-4" /> ปรึกษาฟรี — แจ้งประเทศและประเภทเอกสาร
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}